import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Badge, Card, CardBody, CardHead, LoadError, Skeleton } from './ui.tsx'
import { useApi } from '../lib/api.ts'
import { ago, kickOffTime, odds } from '../lib/format.ts'
import type { FixtureRow, FixturesPayload } from '../../shared/types.ts'

/** "Saturday 17 October" — the games list is grouped by the day they kick off. */
function day(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
}

/**
 * Upcoming games in the leagues we price, soonest first, searchable by team or
 * competition. Every game opens its page, with all its markets once the odds collector
 * has priced it; a game not priced yet is listed all the same, because knowing it is
 * coming is the point.
 */
export function UpcomingGames() {
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  // Search as you type, without a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setQ(search.trim()), 250)
    return () => clearTimeout(id)
  }, [search])

  const { data, error, loading, reload } = useApi<FixturesPayload>(
    `/fixtures${q ? `?q=${encodeURIComponent(q)}` : ''}`,
    60_000,
  )

  const groups: { day: string; games: FixtureRow[] }[] = []
  for (const g of data?.fixtures ?? []) {
    const d = day(g.kickoff)
    if (groups.at(-1)?.day !== d) groups.push({ day: d, games: [] })
    groups.at(-1)!.games.push(g)
  }

  return (
    <Card>
      <CardHead
        title="Upcoming games"
        sub={data?.updatedAt ? `Updated ${ago((Date.now() - Date.parse(data.updatedAt)) / 1000)}` : undefined}
      />
      <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)' }}>
        <div className="input-group" style={{ width: '100%' }}>
          <span className="addon">⌕</span>
          <input
            className="input"
            style={{ flex: 1 }}
            placeholder="Search team or league…"
            aria-label="Search games"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      <CardBody flush>
        {error ? (
          <div style={{ padding: 18 }}>
            <LoadError error={error} onRetry={reload} />
          </div>
        ) : loading && !data ? (
          <div style={{ padding: 18 }}>
            <Skeleton rows={8} />
          </div>
        ) : !data?.fixtures.length ? (
          <div style={{ padding: 24 }} className="u-faint">
            {q ? `No upcoming game matches “${q}”.` : 'No upcoming games.'}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="tbl rows-clickable">
              <thead>
                <tr>
                  <th>Kick-off</th>
                  <th>Game</th>
                  <th>Home</th>
                  <th>Draw</th>
                  <th>Away</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => [
                  <tr key={group.day} className="day-row">
                    <td colSpan={6} className="lbl">
                      {group.day}
                    </td>
                  </tr>,
                  ...group.games.map((g) => <GameRow key={g.eventId} game={g} />),
                ])}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

function GameRow({ game: g }: { game: FixtureRow }) {
  const navigate = useNavigate()
  const open = () => navigate(`/games/${encodeURIComponent(g.eventId)}`)
  const price = (p: number | null | undefined) => (p ? odds(p) : <span className="muted">—</span>)

  return (
    <tr
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter') open()
      }}
      tabIndex={0}
      role="link"
      aria-label={`Open ${g.name}`}
    >
      <td className="u-mono">{kickOffTime(g.kickoff)}</td>
      <td>
        <div className="cell-strong">{g.name}</div>
        <div className="cell-sub">{g.competition}</div>
      </td>
      <td className="u-mono">{price(g.prices?.home)}</td>
      <td className="u-mono">{price(g.prices?.draw)}</td>
      <td className="u-mono">{price(g.prices?.away)}</td>
      <td className="u-right">
        {g.signals > 0 ? (
          <Badge tone="teal">{g.signals} moving</Badge>
        ) : !g.marketId ? (
          <span className="cell-sub">Not priced yet</span>
        ) : null}
      </td>
    </tr>
  )
}
